import { useEffect, useState } from "react";
import { researchApi } from "../api/client";
import type { ResearchRun } from "../types";
import Markdown from "../components/Markdown";
import "./ResearchView.css";
import { useToast } from "../lib/toast";

export default function ResearchView() {
  const toast = useToast();
  const [runs, setRuns] = useState<ResearchRun[]>([]);
  const [topic, setTopic] = useState("");
  const [iterations, setIterations] = useState(3);
  const [selected, setSelected] = useState<ResearchRun | null>(null);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const load = async () => {
    try {
      const list = await researchApi.list();
      setRuns(list);
      // Live-update the selected run when it's still in the list.
      if (selected) {
        const match = list.find((r) => r.id === selected.id);
        if (match) setSelected(match);
      }
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  useEffect(() => {
    load();
    // Task 8.4: back off polling once nothing is actively running — a
    // fixed 4s poll forever burns requests on a page nobody's watching
    // progress on. 4s while any run is pending/running, 15s otherwise.
    const hasActiveRun = runs.some((r) => r.status === "pending" || r.status === "running");
    const interval = hasActiveRun ? 4000 : 15000;
    const i = setInterval(load, interval);
    return () => clearInterval(i);
  }, [selected, runs.length, runs.some((r) => r.status === "pending" || r.status === "running")]);

  const start = async () => {
    if (!topic.trim()) return;
    setStarting(true);
    try {
      await researchApi.start({ topic, config: { iterations } });
      setTopic("");
      load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setStarting(false);
    }
  };

  const open = async (r: ResearchRun) => {
    try {
      const fresh = await researchApi.get(r.id);
      setSelected(fresh);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const cancel = async (r: ResearchRun, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCancelling(true);
    try {
      const updated = await researchApi.cancel(r.id);
      setRuns((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      if (selected?.id === updated.id) setSelected(updated);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setCancelling(false);
    }
  };

  const downloadReport = (r: ResearchRun) => {
    if (!r.result) return;
    try {
      const blob = new Blob([r.result], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const slug = r.topic.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "research";
      const link = document.createElement("a");
      link.href = url;
      link.download = `${slug}.md`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error((e as Error).message || "Could not download report.");
    }
  };

  return (
    <div className="view">
      <div className="view-header">
        <div>
          <h2 className="view-title">🔬 AutoResearch</h2>
          <p className="view-subtitle">Long-running background research</p>
        </div>
      </div>

      <div className="card research-starter">
        <div className="row">
          <input
            className="input"
            placeholder="Research topic…"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && start()}
          />
          <label className="row" style={{ gap: 6 }} title="Number of research iterations (more = deeper, slower)">
            <span className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>Iterations</span>
            <select
              className="select"
              style={{ width: "auto" }}
              aria-label="Iterations"
              value={iterations}
              onChange={(e) => setIterations(Number(e.target.value))}
            >
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button className="btn btn-primary" onClick={start} disabled={starting}>
            {starting ? <span className="spinner" /> : "Start Research"}
          </button>
        </div>
      </div>

      <div className="research-layout">
        <div className="research-list">
          {runs.length === 0 && <div className="empty-state"><h3>No research runs</h3></div>}
          {runs.map((r) => (
            <div
              key={r.id}
              className={`card research-item ${selected?.id === r.id ? "active" : ""}`}
              onClick={() => open(r)}
            >
              <div className="row-between" style={{ marginBottom: 6 }}>
                <strong style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.topic}
                </strong>
                <span className={`tag ${statusTag(r.status)}`}>{r.status}</span>
              </div>
              <ProgressBar value={r.progress} />
              <div className="row-between" style={{ marginTop: 6 }}>
                <div className="dim" style={{ fontSize: 11 }}>
                  {r.current_step || `iterations: ${r.iterations}`}
                </div>
                {isCancellable(r) && (
                  <button className="btn btn-danger btn-sm" onClick={(e) => cancel(r, e)} disabled={cancelling}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="research-detail">
          {!selected && <div className="empty-state"><h3>Select a run</h3><p>Choose a research run to view its results.</p></div>}
          {selected && (
            <div className="card research-detail-card">
              <div className="row-between" style={{ marginBottom: 4 }}>
                <h3 style={{ marginTop: 0, marginBottom: 0 }}>{selected.topic}</h3>
                <div className="row" style={{ gap: 8 }}>
                  {isCancellable(selected) && (
                    <button className="btn btn-danger btn-sm" onClick={(e) => cancel(selected, e)} disabled={cancelling}>
                      Cancel
                    </button>
                  )}
                  {selected.result && (
                    <button className="btn btn-sm" onClick={() => downloadReport(selected)}>
                      Download report
                    </button>
                  )}
                </div>
              </div>
              <div className="row" style={{ gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
                <span className={`tag ${statusTag(selected.status)}`}>{selected.status}</span>
                <span className="tag tag-muted">{selected.model}</span>
                <span className="dim" style={{ fontSize: 11 }}>
                  {Math.round(selected.progress * 100)}% · iter {selected.iterations}
                </span>
              </div>
              <ProgressBar value={selected.progress} />
              {selected.status === "cancelled" && (
                <div className="tag tag-cancelled" style={{ marginTop: 8, display: "inline-block" }}>
                  Cancelled{selected.result ? " — partial result kept" : ""}
                </div>
              )}
              {isCapExhausted(selected) && (
                <div className="tag tag-warning" style={{ marginTop: 8, display: "inline-block" }}>
                  ⚠ Stopped early — iteration/time cap reached, result may be partial
                </div>
              )}
              {selected.status === "failed" && (
                <div className="tag tag-danger" style={{ marginTop: 8, display: "block" }}>
                  Failed: {failureReason(selected)}
                </div>
              )}
              {selected.current_step && selected.status !== "failed" && (
                <div className="muted" style={{ marginTop: 8 }}>{selected.current_step}</div>
              )}
              {selected.result && (
                <>
                  <hr style={{ borderColor: "var(--border)", margin: "16px 0" }} />
                  <Markdown content={selected.result} />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProgressBar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div className="progress-bar">
      <div className="progress-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

function statusTag(s: string): string {
  if (s === "completed") return "tag-success";
  if (s === "running") return "tag-warning";
  if (s === "failed") return "tag-danger";
  if (s === "cancelled") return "tag-cancelled";
  return "tag-muted";
}

function isCancellable(r: ResearchRun): boolean {
  return r.status === "pending" || r.status === "running";
}

// Task 8.4: the backend signals the wall-clock/iteration cap via
// current_step text (no dedicated schema field) — see
// research_service.py's `cap_exhausted` handling.
function isCapExhausted(r: ResearchRun): boolean {
  return r.status === "completed" && !!r.current_step?.includes("wall-clock cap reached");
}

function failureReason(r: ResearchRun): string {
  const step = r.current_step || "";
  return step.startsWith("Failed: ") ? step.slice("Failed: ".length) : step || "unknown error";
}